import type { Doc } from '@blocksuite/affine/store';

type DatabaseDocResourcePort = {
  addDoc(docId: string, source: 'database-subdoc'): void;
  releaseDoc(docId: string, source: 'database-subdoc'): Promise<void>;
  debugDocState?(docId: string): Promise<Array<{ reasons: string[] }>>;
};
const owners = new WeakMap<DatabaseDocResourcePort, Map<string, number>>();
const acknowledged = new WeakMap<DatabaseDocResourcePort, Set<string>>();

export function acknowledgeDatabaseSubdoc(
  port: DatabaseDocResourcePort,
  docId: string
): void {
  let docs = acknowledged.get(port);
  if (!docs) {
    docs = new Set();
    acknowledged.set(port, docs);
  }
  docs.add(docId);
}

/** Sync reasons are sets; aggregate independent window/page/edit owners here. */
export function retainDatabaseSubdoc(
  port: DatabaseDocResourcePort,
  docId: string,
  onIdle?: () => void
): () => void {
  acknowledged.get(port)?.delete(docId);
  let counts = owners.get(port);
  if (!counts) {
    counts = new Map();
    owners.set(port, counts);
  }
  const count = counts.get(docId) ?? 0;
  counts.set(docId, count + 1);
  if (!count) port.addDoc(docId, 'database-subdoc');
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const remaining = (counts.get(docId) ?? 1) - 1;
    if (remaining) counts.set(docId, remaining);
    else {
      counts.delete(docId);
      void port
        .releaseDoc(docId, 'database-subdoc')
        .then(() => {
          if (!counts.has(docId)) onIdle?.();
        })
        .catch(error =>
          console.error('Failed to release database sync reference', error)
        );
    }
  };
}

/** Only called after an acknowledged write's last resource reference was released. */
export async function releaseAcknowledgedSystemDocCache(
  port: DatabaseDocResourcePort,
  collection: {
    getDoc(docId: string): Doc | null;
    releaseSystemDocCache?: (docId: string, expected: Doc) => boolean;
  },
  docId: string
): Promise<boolean> {
  if (
    !acknowledged.get(port)?.has(docId) ||
    owners.get(port)?.has(docId) ||
    !port.debugDocState ||
    !collection.releaseSystemDocCache
  )
    return false;
  const expected = collection.getDoc(docId);
  if (!expected) return false;
  const states = await port.debugDocState(docId);
  // Navigation, projection, import, export and other sync reasons own their
  // references independently. Recheck local owners after the asynchronous read.
  if (
    !acknowledged.get(port)?.has(docId) ||
    owners.get(port)?.has(docId) ||
    states.some(state => state.reasons.length)
  )
    return false;
  const released = collection.releaseSystemDocCache(docId, expected);
  if (released) {
    acknowledged.get(port)?.delete(docId);
    // Sync barrier mark for scripts/benchmarks/database-import-metrics.mjs: the local cache of this doc is gone.
    const name = `app-base-sync-barrier:${docId}:cache-released`;
    performance.clearMarks(name);
    performance.mark(name, { detail: { phase: 'cache-released', docId } });
  }
  return released;
}
